/**
 * /api/views/* — per-user, per-board view preference reads + writes.
 *
 * Backs the View switcher on /board/$boardId. Each board has at most
 * one ``ViewPreference`` row per user; consumers PUT the full desired
 * state on every change (the gateway upserts). Reading falls back to
 * the kanban default when no row exists.
 */
import type {
  BulkAction,
  BulkActionResult,
  CardDependency,
  CreateCardDependencyInput,
  UpsertViewPreferenceInput,
  ViewPreference,
} from '@chaos/types/views';

import { apiJson } from './client';

interface PreferenceResponse {
  preference: ViewPreference | null;
}

export const viewsApi = {
  getPreference(boardId: string): Promise<ViewPreference | null> {
    return apiJson<PreferenceResponse>(`/views/boards/${boardId}`).then((r) => r.preference);
  },
  upsertPreference(boardId: string, input: UpsertViewPreferenceInput): Promise<ViewPreference> {
    return apiJson<{ preference: ViewPreference }>(`/views/boards/${boardId}`, {
      method: 'PUT',
      json: input,
    }).then((r) => r.preference);
  },
  resetPreference(boardId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/views/boards/${boardId}`, {
      method: 'DELETE',
    });
  },
};

export const viewsKeys = {
  all: ['views'] as const,
  preference: (boardId: string) => [...viewsKeys.all, 'preference', boardId] as const,
};

// ── Bulk + dependencies live under /api/kanban/* but consumed mostly
// from List + Gantt views; we export them in the same module to keep
// callers tidy.
export const bulkApi = {
  run(action: BulkAction): Promise<BulkActionResult> {
    return apiJson<BulkActionResult>('/kanban/cards/bulk', {
      method: 'POST',
      json: action,
    });
  },
};

export const dependenciesApi = {
  list(boardId: string): Promise<CardDependency[]> {
    return apiJson<{ dependencies: CardDependency[] }>(
      `/kanban/boards/${boardId}/dependencies`,
    ).then((r) => r.dependencies);
  },
  create(cardId: string, input: CreateCardDependencyInput): Promise<CardDependency> {
    return apiJson<{ dependency: CardDependency }>(`/kanban/cards/${cardId}/dependencies`, {
      method: 'POST',
      json: input,
    }).then((r) => r.dependency);
  },
  delete(cardId: string, depId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/kanban/cards/${cardId}/dependencies/${depId}`, {
      method: 'DELETE',
    });
  },
};

export const dependenciesKeys = {
  all: ['dependencies'] as const,
  forBoard: (boardId: string) => [...dependenciesKeys.all, 'board', boardId] as const,
};
